import { TierLadderItem } from "entities/tier";

export type UserAuthType = "password" | "oauth";

export enum UserTier {
  STANDARD = "standard",
  BRONZE = "bronze",
  SILVER = "silver",
  GOLD = "gold",
  DIAMOND = "diamond",
  PLATINUM = "platinum",
}

export interface DepositsUsdt {
  currency: string;
  total_all_time: string;
  updated_at: string;
}

export interface UserLimits {
  max_open_orders_usdt: string;
  open_orders_usdt_sum: string;
}

export interface UserStatus {
  benefits: string;
  benefits_items: string[];
  demo_unlimited: boolean;
  deposit_window_days: number;
  deposits_usdt_in_window: string;
  limits: {
    demo: UserLimits;
    real: UserLimits;
  };
  max_expiration_sec: number;
  min_expiration_sec: number;
  payout_bonus_pct: string;
  switch_time: boolean;
  thresholds_version: number;
  tier: UserTier;
  tier_display_name: string;
  tier_ladder: TierLadderItem[];
}

export interface UserBalances {
  demo: string;
  real: string;
}

export interface Account {
  id: number;
  last_login: string;
  last_login_city: string;
  last_login_country: string;
  last_login_ip: string;
  last_login_region: string;
  registered_on: string;
}

export interface UserAvatar {
  has_custom: boolean;
  url: string | null;
}

export interface User {
  auth_type: UserAuthType;
  balances: UserBalances;
  created_at: string;
  deposits_usdt: DepositsUsdt;
  email: string;
  id: number;
  last_login: string;
  last_login_city: string;
  last_login_country: string;
  last_login_ip: string;
  last_login_region: string;
  oauth_provider: string;
  registered_on: string;
  settings: {};
  status: UserStatus;
  username: string;
  telegram_2fa: TwoFactorAuthentication;
  whatsapp_2fa: TwoFactorAuthenticationWithWhatsapp;
  // Право организовывать промо «Колесо»; старый бэк поле не отдаёт.
  promo_actions_wheel?: boolean;
}

export interface UserDTO {
  account: Account & {
    tier: {
      code: UserTier;
      tier_display_name: string;
    };
  };
  auth: {
    auth_type: UserAuthType;
    can_change_password: boolean;
    can_set_password: boolean;
    oauth_provider: string;
  };
  avatar: UserAvatar;
  contact_email: {
    editable: boolean;
    pending: string | null;
    value: string | null;
    verified: boolean;
  };
  display_name: {
    editable: boolean;
    username: string;
  };
  legal_name: {
    editable: boolean;
    first_name: string | null;
    last_name: string | null;
  };
  login_email: {
    is_synthetic: boolean;
    value: string;
  };
  security: {
    telegram_2fa: TwoFactorAuthentication;
    whatsapp_2fa: TwoFactorAuthenticationWithWhatsapp;
  };
}

export interface TwoFactorAuthentication {
  linked: boolean;
  enabled: boolean;
  linked_at: string | null;
}

export type TwoFactorAuthenticationStatus = "none" | "pending" | "verified";

export interface TwoFactorAuthenticationWithWhatsapp extends TwoFactorAuthentication {
  phone_masked: string | null;
  status: TwoFactorAuthenticationStatus;
}

export interface AuthUserData {
  email: string;
  id: number;
  user_hash: string;
  username: string;
}

export interface AuthResponse {
  access_token: string;
  user: AuthUserData;
}

export interface RequiresTwoFactorResponse {
  requires_2fa: true;
  challenge_id: string;
  methods: string[];
  expires_at: string;
  message: string;
}

export type LoginResult = AuthResponse | RequiresTwoFactorResponse;

export const isRequiresTwoFactor = (
  result: LoginResult,
): result is RequiresTwoFactorResponse => "requires_2fa" in result;

export interface UserWallet {
  address: string;
  balance: string;
  currency: string;
  network: string;
  qr_image: string;
}

export interface DepositsHistory {
  deposits: {
    amount: string;
    amount_usdt: string;
    created_at: string;
    currency: string;
    id: number;
    to_address: string;
    tx_hash: string;
  }[];
  range: {
    date_from: string;
    date_to: string;
    scope: string;
  };
}

export interface WithdrawalDetails {
  amount: string;
  amount_usdt: string;
  completed_tx_hash: string | null;
  created_at: string;
  currency: string;
  destination_address: string;
  id: number;
  network: string;
  status: WithdrawalStatus;
  updated_at: string;
}

export interface WithdrawalsHistory {
  range: {
    date_from: string;
    date_to: string;
    scope: string;
  };
  withdrawals: WithdrawalDetails[];
}

export type WithdrawalStatus =
  "in_progress" | "completed" | "cancelled" | "rejected";
